/** OpenAI/Realtime function names must match `^[a-zA-Z0-9_-]{1,64}$`. */
const MAX_TOOL_NAME_LENGTH = 64;

/**
 * Convert a host tool name into a model-facing function name.
 *
 * We sanitise to the allowed function-name charset (rather than hashing to an opaque
 * `yt_<hex>`), so the model keeps the semantic signal of a readable name — e.g.
 * `orders.list` → `orders_list`.
 *
 * This is a pure per-name transform; uniqueness across a manifest (and avoiding the
 * reserved `redirect` name / `mcp__` namespace) is handled by the caller via
 * {@link uniqueToolId}. Each runtime (the chat-ui iframe and this SDK's voice session)
 * decorates and reverse-maps with its own manifest, so the ids never need to match
 * across paths — only to be self-consistent within one.
 */
export function generateToolId(originalName: string): string {
  const sanitized = originalName.replace(/[^A-Za-z0-9_-]/g, "_").slice(0, MAX_TOOL_NAME_LENGTH);
  return sanitized.length > 0 ? sanitized : "tool";
}

/** The MCP namespace prefix the voice/chat dispatchers route on. */
const MCP_NAMESPACE_PREFIX = "mcp__";

/**
 * Resolve a collision-free, model-safe id for a host tool name, given the ids already
 * taken in this manifest (seed `used` with reserved names like `redirect`). Host ids must
 * never start with the `mcp__` namespace, which the dispatcher routes on.
 */
export function uniqueToolId(originalName: string, used: Set<string>): string {
  let base = generateToolId(originalName);
  if (base.startsWith(MCP_NAMESPACE_PREFIX)) {
    base = `t_${base}`.slice(0, MAX_TOOL_NAME_LENGTH);
  }
  if (!used.has(base)) return base;

  for (let i = 2; i < 1000; i++) {
    const suffix = `_${i}`;
    const candidate = `${base.slice(0, MAX_TOOL_NAME_LENGTH - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  // Pathological fallback — vanishingly unlikely in practice.
  return `${base.slice(0, 56)}_${used.size}`;
}
